import { useEffect, useMemo, useRef, useState } from 'react';
import { PhotoModel } from '../../../types/protocols';
import { ImageSize, getImagePathWithSize, resolveFirebaseImageUrl } from '../../../Helpers/ImageLoader';
import { PhotoGroupItem } from '../../WorkOrders/components/shared';
import { orderPhotos } from '../../../Helpers/photoOrdering';

/**
 * Resolves a `{[key]: PhotoModel}` map into `PhotoGroupItem`s with real URLs
 * for the context-free `PhotoGroup` leaf. `url` is the original; `thumbnailUrl`
 * is the 200x200 variant when it exists and is left out otherwise, so a photo
 * whose thumbnail has not been generated yet (just uploaded) shows the original
 * instead of staying invisible. Resolved URLs are cached per photo key for the
 * hook's lifetime, so a silent refetch of the work order does not re-resolve.
 * Items come newest first (`orderPhotos`).
 */
export function useResolvedPhotoUrls(photos: { [key: string]: PhotoModel } | undefined): PhotoGroupItem[] {
    const cache = useRef<Record<string, { url: string; thumbnailUrl?: string }>>({});
    const [, bump] = useState(0);

    const ordered = useMemo(() => orderPhotos(photos || {}), [photos]);
    const keys = useMemo(() => ordered.map((photo) => photo.key), [ordered]);
    const signature = keys.join(',');

    useEffect(() => {
        if (!photos) return;
        const pending = keys.filter((key) => !cache.current[key] && photos[key]?.storagePath);
        if (pending.length === 0) return;
        Promise.all(pending.map(async (key) => {
            const storagePath = photos[key].storagePath;
            try {
                const thumbnailPath = getImagePathWithSize(storagePath, ImageSize.THUMBNAIL_SMALL);
                const [url, thumbnailUrl] = await Promise.all([
                    resolveFirebaseImageUrl(storagePath),
                    thumbnailPath ? resolveFirebaseImageUrl(thumbnailPath).catch(() => undefined) : undefined
                ]);
                cache.current[key] = { url, thumbnailUrl };
            } catch (err) {
                console.error('Failed to resolve photo URL:', err);
            }
        })).then(() => {
            // Always re-render, even if the photo set changed meanwhile: the
            // cache entries filled here would otherwise wait for the next change.
            bump((n) => n + 1);
        });
        // `signature` stands in for `keys`/`photos` so a silent refetch with the
        // same photo set does not re-run.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);

    return ordered
        .filter((photo) => cache.current[photo.key])
        .map((photo) => ({
            key: photo.key,
            url: cache.current[photo.key].url,
            thumbnailUrl: cache.current[photo.key].thumbnailUrl,
            date: photo.date
        }));
}
